import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { type ForwardedRef, forwardRef, useRef, useState } from 'react';

import { Box } from '../../box';
import { Flex } from '../../flex';

type ColorPickerProps = Omit<TextFieldProps, 'type' | 'multiline' | 'minRows' | 'maxRows'> & {
  defaultColor?: string;
};

const COLOR_PICKER_STYLE = {
  width: 46,
  height: 44,
  marginRight: 0.75,
  '.MuiOutlinedInput-input': {
    padding: 0.25,
    '&::-webkit-color-swatch': {
      borderRadius: 0.5,
      border: 'none',
    },
  },
};

const HEX_COLOR_REGEX = /^([0-9a-fA-F]{6})$|^#([0-9a-fA-F]{6})$/;

const ColorPicker = forwardRef((props: ColorPickerProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    label,
    helperText,
    value,
    defaultValue,
    defaultColor = '',
    onChange,
    onBlur,
    ...restProps
  } = props;
  const { current: isControlled } = useRef<boolean>(!!value);
  const [colorValue, setColorValue] = useState<string>((value ?? defaultValue) as string);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!!e.target.value && e.target.value[0] !== '#') e.target.value = '#' + e.target.value;

    if (!isControlled) setColorValue(e.target.value);

    onChange?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value) e.target.value = defaultColor;

    if (!!e.target.value && e.target.value[0] !== '#') e.target.value = '#' + e.target.value;

    const isHexColor = HEX_COLOR_REGEX.test(e.target.value);

    if (!isHexColor) e.target.value = '';
    if (!isControlled) setColorValue(e.target.value);

    if (!onBlur) {
      onChange?.(e);
    }
    onBlur?.(e);
  };

  const preventInvalidColor = (e: React.FormEvent<HTMLDivElement>) => {
    const colorEl = e.target as HTMLInputElement;
    colorEl.value = colorEl.value.replace(/[^0-9a-fA-F]/g, '');

    if (colorEl.value.length >= 6) colorEl.value = colorEl.value.slice(0, 6);
  };

  const {
    color,
    error: hasError,
    disabled: isDisabled,
    required: isRequired,
    focused: isFocused,
  } = props;
  const inputState = {
    color,
    error: hasError,
    disabled: isDisabled,
    required: isRequired,
    focused: isFocused,
  };

  const rawColor = (value as string) ?? colorValue;
  const hexColor = rawColor[0] !== '#' ? rawColor : rawColor.slice(1);

  return (
    <Box>
      <InputLabel {...inputState}>{label}</InputLabel>
      <Flex sx={{ mt: 0.5 }}>
        <TextField
          sx={COLOR_PICKER_STYLE}
          {...inputState}
          type='color'
          value={rawColor}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <TextField
          {...restProps}
          ref={ref}
          onChange={handleChange}
          onBlur={handleBlur}
          onInput={preventInvalidColor}
          value={hexColor}
          InputProps={{
            startAdornment: <InputAdornment position='start'>#</InputAdornment>,
          }}
        />
      </Flex>
      <FormHelperText {...inputState}>{helperText}</FormHelperText>
    </Box>
  );
});

ColorPicker.displayName = 'ColorPicker';

export { ColorPicker, HEX_COLOR_REGEX };
export type { ColorPickerProps };
